import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Save, ArrowLeft, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ProjectForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [searchParams] = useSearchParams();
  const isProto = searchParams.get("proto") === "1";
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    project_name: "",
    customer_id: "",
    end_customer: "",
    customer_due_date: "",
    quote_validity_date: "",
    remarks: "",
    default_scrap_recovery_factor: 0.9,
    default_tool_maintenance_pct: 10,
    unit_id: "",
    category: "",            // "2W" | "4W" | "Other" — vehicle segment (mandatory)
    category_other: "",      // free text when category === "Other"
    is_prototype: isProto,   // persist Proto Project so it stays a prototype after save
  });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    if (user?.role === "admin") {
      api.get("/units").then((r) => setUnits(r.data || [])).catch(() => {});
    }
  }, [user?.role]);

  // Edit mode — load the existing project into the form.
  useEffect(() => {
    if (!isEdit) return;
    api.get(`/projects/${id}`).then((r) => {
      const p = r.data || {};
      setForm({
        project_name: p.project_name || "",
        customer_id: p.customer_id || "",
        end_customer: p.end_customer || "",
        customer_due_date: p.customer_due_date || "",
        quote_validity_date: p.quote_validity_date || "",
        remarks: p.remarks || "",
        default_scrap_recovery_factor: p.default_scrap_recovery_factor ?? 0.9,
        default_tool_maintenance_pct: p.default_tool_maintenance_pct ?? 10,
        unit_id: p.unit_id || "",
        category: p.category || "",
        category_other: p.category_other || "",
        is_prototype: p.is_prototype || false,
      });
    }).catch(() => toast.error("Could not load project"));
  }, [id, isEdit]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.project_name || !form.customer_id) {
      toast.error("Project name and customer are required.");
      return;
    }
    if (!form.category) {
      toast.error("Please select Category (2W / 4W).");
      return;
    }
    if (form.category === "Other" && !(form.category_other || "").trim()) {
      toast.error("Please specify the 'Other' category.");
      return;
    }
    if (user?.role === "admin" && !form.unit_id) {
      toast.error("Pick a unit for this project.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/projects/${id}`, form);
        toast.success("Project updated");
        nav(`/projects/${id}`, { replace: true });
      } else {
        const r = await api.post("/projects", form);
        toast.success(`Created ${r.data.project_number}`);
        nav(`/projects/${r.data.project_id}`, { replace: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEdit ? "Update failed" : "Create failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-3xl mx-auto" data-testid="project-form-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/projects")} className="p-2 hover:bg-slate-100 rounded-md text-slate-500">
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Briefcase size={20} className="text-orange-600" /> {isEdit ? "Edit Project" : "New Project"}
          </h1>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-semibold rounded-md flex items-center gap-1.5"
          data-testid="project-save"
        >
          <Save size={14} /> {saving ? "Saving…" : (isEdit ? "Save Changes" : "Create Project")}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
        <Field label="Project Name *">
          <input
            value={form.project_name}
            onChange={(e) => update("project_name", e.target.value)}
            placeholder="e.g. BorgWarner – April Inquiry – 10 parts"
            className="input"
            data-testid="project-name"
          />
        </Field>
        <Field label="Customer *">
          <select
            value={form.customer_id}
            onChange={(e) => update("customer_id", e.target.value)}
            className="input"
            data-testid="project-customer"
          >
            <option value="">Select customer…</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
            ))}
          </select>
        </Field>
        {user?.role === "admin" && (
          <Field label="Unit *">
            <select
              value={form.unit_id}
              onChange={(e) => update("unit_id", e.target.value)}
              className="input"
              data-testid="project-unit"
            >
              <option value="">Select unit…</option>
              {units.map((u) => (
                <option key={u.unit_id} value={u.unit_id}>{u.unit_code} — {u.unit_name}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label="End Customer (optional)">
          <input
            value={form.end_customer}
            onChange={(e) => update("end_customer", e.target.value)}
            placeholder="e.g. AISIN"
            className="input"
          />
        </Field>
        <Field label="Category (2W / 4W) *">
          <div className="w-full flex items-stretch border border-slate-300 rounded-md bg-white overflow-hidden focus-within:ring-2 focus-within:ring-orange-500">
            <select
              required
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
              className={`bg-transparent border-0 focus:ring-0 focus:outline-none text-sm py-2 pl-3 pr-1 ${form.category === "Other" ? "w-24 shrink-0" : "w-full"}`}
              data-testid="project-category"
            >
              <option value="">Select segment…</option>
              <option value="2W">2W (2-Wheeler)</option>
              <option value="4W">4W (4-Wheeler)</option>
              <option value="Other">Other</option>
            </select>
            {form.category === "Other" && (
              <input
                type="text"
                value={form.category_other || ""}
                onChange={(e) => update("category_other", e.target.value)}
                className="flex-1 min-w-0 border-0 border-l border-slate-200 bg-transparent focus:ring-0 focus:outline-none text-sm py-2 px-2"
                placeholder="specify (e.g. 3-Wheeler, CV, Off-road)"
                data-testid="project-category-other"
              />
            )}
          </div>
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Customer Due Date">
            <input
              type="date"
              value={form.customer_due_date || ""}
              onChange={(e) => update("customer_due_date", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Quote Validity">
            <input
              type="date"
              value={form.quote_validity_date || ""}
              onChange={(e) => update("quote_validity_date", e.target.value)}
              className="input"
            />
          </Field>
        </div>
        <div className={isProto ? "" : "grid grid-cols-1 md:grid-cols-2 gap-3"}>
          <Field label="Default Scrap Recovery Factor (0-1)">
            <input
              type="number" step="0.01" min="0" max="1"
              value={form.default_scrap_recovery_factor}
              onChange={(e) => update("default_scrap_recovery_factor", parseFloat(e.target.value) || 0.9)}
              className="input"
            />
          </Field>
          {!isProto && (
            <Field label="Default Tool Maintenance (%)">
              <input
                type="number" step="0.1" min="0" max="100"
                value={form.default_tool_maintenance_pct}
                onChange={(e) => update("default_tool_maintenance_pct", parseFloat(e.target.value) || 10)}
                className="input"
              />
            </Field>
          )}
        </div>
        <Field label="Remarks (optional)">
          <textarea
            rows={3}
            value={form.remarks}
            onChange={(e) => update("remarks", e.target.value)}
            placeholder="Internal notes about this RFQ…"
            className="input"
          />
        </Field>
        <p className="text-xs text-slate-500 italic">
          The project will inherit your app's default overheads. You can edit them per project on the detail page.
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
